import { parseDateLocal } from './dateService';

/**
 * Gemeinsames Aussehen der Kalendermails (Urlaubsvertretung, Projekt-Kickoff).
 *
 * Beide Mails sollen für den Empfänger gleich aussehen: gleicher Kopf mit
 * grüner Linie, gleiche Tabelle, gleiche Outlook-Anleitung, gleiche Fußzeile.
 * Deshalb liegen die Bausteine hier und nicht doppelt in den Mailern — sonst
 * driften die Mails beim nächsten Umbau auseinander.
 *
 * Die Bausteine liefern HTML-Schnipsel mit fester Einrückung. Die
 * Vertretungsmail muss Zeichen für Zeichen gleich bleiben; wer hier Leerzeichen
 * oder Zeilenumbrüche ändert, ändert auch sie.
 *
 * Konvention: Parameter mit Nutzerdaten (Projektname, Adresse …) maskieren die
 * Bausteine selbst. Parameter, die als HTML dokumentiert sind (Titel, Spalten,
 * Zusatzinhalt), übernimmt der Baustein unverändert — der Aufrufer maskiert.
 */

/** Minimal-Escaping für die HTML-Mail (Projektnamen kommen aus Nutzereingaben). */
export const esc = (value: string | undefined | null): string =>
    String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');

/** `YYYY-MM-DD` → `TT.MM.JJJJ`, ungültig oder leer → `-`. */
export const formatDate = (value: string | undefined): string => {
    const d = parseDateLocal(value || '');
    return d ? d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-';
};

/** Kurzer Wochentag ohne Punkt („Di“); der Aufrufer setzt den Punkt. */
export const formatWeekday = (value: string): string => {
    const d = parseDateLocal(value);
    return d ? d.toLocaleDateString('de-DE', { weekday: 'short' }) : '';
};

/** Zellenstil der Datenzeilen. */
export const TD = 'padding:10px 12px;border-bottom:1px solid #e2e8f0;vertical-align:top;';
/** Stil der Spaltenköpfe. */
export const TH = 'padding:8px 12px;text-align:left;font-size:11px;letter-spacing:.06em;text-transform:uppercase;color:#64748b;border-bottom:2px solid #cbd5e1;';

/** Rot: etwas fehlt oder ist zu spät (Baustart fehlt, Frist überschritten). */
export const FARBE_FEHLER = '#b91c1c';
/** Orange: Handarbeit nötig (Turnus manuell, Überschneidung). */
export const FARBE_WARNUNG = '#b45309';
/** Grau: nichts einzutragen. */
export const FARBE_LEER = '#94a3b8';

const RAHMEN = "font-family:'Segoe UI',Tahoma,Geneva,Verdana,sans-serif;color:#334155;line-height:1.6;max-width:760px;";

export interface MailRahmen {
    /** Überschrift im Kopf (HTML). */
    titel: string;
    /** Graue Zeile unter der Überschrift (HTML), z. B. Zeitraum oder Datum. */
    unterzeile: string;
    /**
     * Inhaltsblöcke zwischen Kopf und Fußzeile (HTML), je Block eine
     * Leerzeile Abstand im Quelltext.
     */
    bloecke: string[];
    /** Zweiter Satz der Fußzeile (HTML), z. B. wohin Antworten gehen. */
    antwortSatz: string;
}

/** Ganze Mail: Kopf mit grüner Linie, Inhaltsblöcke, Fußzeile. */
export const renderMailRahmen = ({ titel, unterzeile, bloecke, antwortSatz }: MailRahmen): string =>
    `<div style="${RAHMEN}">
    <div style="padding-bottom:14px;border-bottom:3px solid #059669;margin-bottom:20px;">
        <h2 style="color:#065f46;margin:0;font-size:20px;">${titel}</h2>
        <div style="color:#64748b;font-size:13px;margin-top:4px;">${unterzeile}</div>
    </div>

    ${bloecke.join('\n\n    ')}

    <div style="font-size:11px;color:#94a3b8;border-top:1px solid #e2e8f0;padding-top:14px;">
        Diese E-Mail wurde automatisch über die <strong>SiGeKo Smart Report Cloud</strong> versandt.
        ${antwortSatz}
    </div>
</div>`;

/**
 * Tabelle mit Spaltenköpfen. `spalten` sind HTML; `zeilen` sind die fertigen
 * `<tr>`-Zeilen (siehe `TD` und `renderProjektZelle`).
 */
export const renderTabelle = (spalten: string[], zeilen: string): string =>
    `<table style="width:100%;border-collapse:collapse;font-size:14px;margin-bottom:24px;">
        <thead>
            <tr>
${spalten.map(s => `                <th style="${TH}">${s}</th>`).join('\n')}
            </tr>
        </thead>
        <tbody>${zeilen}</tbody>
    </table>`;

/**
 * Inhalt der Projektzelle: Name fett, Nummer in Festbreite, Adresse klein.
 * Nummer und Adresse entfallen, wenn sie leer sind. Maskiert selbst.
 */
export const renderProjektZelle = (name: string, nummer?: string, adresse?: string): string =>
    `<div style="font-weight:700;color:#0f172a;">${esc(name)}</div>
                ${nummer ? `<div style="font-family:monospace;font-size:11px;color:#64748b;">${esc(nummer)}</div>` : ''}
                ${adresse ? `<div style="font-size:12px;color:#64748b;">${esc(adresse)}</div>` : ''}`;

/** Die drei Schritte, wie der Empfänger die angehängte ICS in Outlook importiert. */
const OUTLOOK_SCHRITTE: ReadonlyArray<string> = [
    'Angehängte Datei <strong>.ics</strong> speichern und doppelt anklicken.',
    'Outlook fragt, wie die Datei geöffnet werden soll &ndash; <strong>&bdquo;In Kalender importieren&ldquo;</strong> wählen.',
    'Alternativ über das Menü: <em>Datei &rarr; Öffnen und Exportieren &rarr; Importieren/Exportieren &rarr; iCalendar- (ICS) oder vCalendar-Datei importieren</em>.',
];

export interface OutlookKastenOptionen {
    /** Weitere nummerierte Schritte nach den drei Outlook-Schritten (HTML). */
    zusatzSchritte?: string[];
    /** Zusatzinhalt unter der Liste (HTML), z. B. `renderKastenHinweis(…)`. */
    zusatz?: string;
}

/** Grüner Kasten mit fetter Überschrift; `titel` und `inhalt` sind HTML. */
export const renderGruenerKasten = (titel: string, inhalt: string): string =>
    `<div style="background:#f0fdf4;border:1px solid #bbf7d0;border-radius:10px;padding:16px 18px;margin-bottom:20px;">
        <div style="font-weight:700;color:#065f46;margin-bottom:8px;">${titel}</div>
        ${inhalt}
    </div>`;

/** Grüner Kasten „Termine in Outlook übernehmen“ mit der Import-Anleitung. */
export const renderOutlookKasten = ({ zusatzSchritte = [], zusatz }: OutlookKastenOptionen = {}): string =>
    renderGruenerKasten(
        'Termine in Outlook übernehmen',
        `<ol style="margin:0;padding-left:18px;font-size:14px;color:#334155;">
${[...OUTLOOK_SCHRITTE, ...zusatzSchritte].map(s => `            <li>${s}</li>`).join('\n')}
        </ol>${zusatz ? `\n        ${zusatz}` : ''}`,
    );

/** Kleiner grauer Absatz im grünen Kasten; je Eintrag eine Quelltextzeile (HTML). */
export const renderKastenHinweis = (zeilen: string[]): string =>
    `<div style="font-size:12px;color:#475569;margin-top:10px;">
${zeilen.map(z => `            ${z}`).join('\n')}
        </div>`;
